/**
 * Drivers as rows, the next two hours as columns.
 *
 * It answers the question the queue cannot: not who is free now, but who will be free in
 * eight minutes. Reading down the rows is reading forward in time — busy drivers first by
 * when they come free, then whoever has been free longest, then breaks.
 *
 * Every percentage on this screen is worked out by `layout.ts`; this file only draws.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderDetail } from '../orders/OrderDetail';
import { OrderEntry } from '../orders/OrderEntry';
import { ordersApi } from '../orders/api';
import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useDispatch } from '../shell/dispatchContext';
import { useTenantFormats } from '../shell/formats';
import { usePhone } from '../shell/softphoneContext';
import {
  Axis,
  Bar,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Icon,
  Lane,
  Lead,
  Marker,
  Note,
  Timeline,
  Track,
  cx,
} from '../ui';
import { timelineApi, type TimelineSnapshot } from './api';
import {
  bookedLane,
  driverRows,
  secondsLeft,
  strip,
  timelineWindow,
  waitingLane,
} from './layout';

/** How often the snapshot is refetched when nothing has happened. */
const EVERY = 30_000;

export function TimelineView() {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const { selectedOrder, setSelectedOrder, fromCall, onCreated, lastTaken } = useDispatch();

  const [snapshot, setSnapshot] = useState<TimelineSnapshot | null>(null);
  const [failed, setFailed] = useState(false);
  const [taking, setTaking] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // The previous snapshot stays on screen while this is in flight. A timeline that
      // blanks every thirty seconds is a timeline nobody can read.
      setSnapshot(await timelineApi.snapshot());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    void refresh();

    const timer = globalThis.setInterval(() => void refresh(), EVERY);

    return () => globalThis.clearInterval(timer);
  }, [refresh]);

  /**
   * Never on `driverMoved`.
   *
   * That event arrives once per position report per driver — several a second across a
   * fleet — and nothing on this screen moves when a car does. The timeline is about time,
   * not about position, so it listens only to the two events that change one.
   */
  useDispatcherHub({
    onOrderChanged: () => void refresh(),
    onDriverShiftChanged: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  const open = useCallback(
    async (orderId: string) => {
      setSelectedOrder(await ordersApi.get(orderId));
    },
    [setSelectedOrder],
  );

  if (snapshot === null) {
    return <EmptyState>{failed ? t('timeline.unavailable') : t('app.loading')}</EmptyState>;
  }

  const window = timelineWindow(snapshot);
  const rows = driverRows(snapshot);
  const waiting = waitingLane(snapshot);
  const booked = bookedLane(snapshot);

  return (
    <div className="flex flex-col gap-3 p-3 flex-1 min-h-0">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
        {/* Wrapped, so that a firm with no telephone still leaves the counts card on the
            right rather than letting it slide into the empty first column. */}
        <div>
          <CallStrip onNewOrder={() => setTaking(true)} />
        </div>
        <Counts snapshot={snapshot} />
      </div>

      <Card className="flex-1 min-h-0 flex flex-col">
        <CardHeader
          title={
            <>
              {t('timeline.title')}
              <span className="text-secondary text-ink-3 font-normal">
                · {t('timeline.now', { time: formats.time(snapshot.asOf) })}
              </span>
            </>
          }
          actions={<Legend />}
        />

        {snapshot.drivers.length === 0 ? (
          <EmptyState>{t('timeline.nobody')}</EmptyState>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Timeline>
              <Lane
                className="h-7"
                label={
                  <span className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
                    {t('timeline.driver')}
                  </span>
                }
                aside={
                  <span className="text-secondary text-ink-3">
                    {t('timeline.onShift', { count: snapshot.drivers.length })}
                  </span>
                }
              />
              <Axis
                nowPct={window.nowPct}
                ticks={window.ticks.map((tick) => ({
                  pct: tick.pct,
                  label: formats.time(new Date(tick.at).toISOString()),
                }))}
              />

              {/* The two lanes above the drivers: everything nobody is going to yet. */}
              <Lane
                label={
                  <span className="text-caps font-semibold tracking-[0.06em] uppercase text-danger">
                    {t('timeline.waiting')}
                  </span>
                }
                aside={
                  <span className="text-secondary text-ink-3">
                    {t('timeline.orders', { count: waiting.length })}
                  </span>
                }
              />
              <Track nowPct={window.nowPct} ticks={window.ticks.map((tick) => tick.pct)}>
                {waiting.map((bar) => (
                  <Bar
                    key={bar.order.orderId}
                    from={bar.from}
                    to={bar.to}
                    tone={bar.tone}
                    title={bar.order.pickupAddress}
                    onClick={() => void open(bar.order.orderId)}
                  >
                    <span className="font-mono font-semibold tracking-[0.03em]">
                      {bar.order.reference}
                    </span>
                    <span className="ml-1.5">
                      {t('queue.minutes', {
                        minutes: Math.max(0, Math.round((window.now - bar.since) / 60_000)),
                      })}
                      {bar.order.needsAttention && ` · ${t('queue.needsAttention')}`}
                    </span>
                  </Bar>
                ))}
              </Track>

              <Lane
                label={
                  <span className="text-caps font-semibold tracking-[0.06em] uppercase text-brand">
                    {t('timeline.booked')}
                  </span>
                }
                aside={
                  <span className="text-secondary text-ink-3">
                    {t('timeline.inWindow', { count: booked.bars.length })}
                    {booked.beyond > 0 && ` · ${t('timeline.beyond', { count: booked.beyond })}`}
                  </span>
                }
              />
              <Track nowPct={window.nowPct} ticks={window.ticks.map((tick) => tick.pct)}>
                {booked.bars.map((entry) => (
                  <span key={entry.booking.orderId} className="contents">
                    <Lead
                      from={entry.lead.from}
                      to={entry.lead.to}
                      title={t('timeline.searchStarts', {
                        time: formats.time(entry.booking.searchStartsAt),
                      })}
                    />
                    <Bar
                      from={entry.bar.from}
                      to={entry.bar.to}
                      tone={entry.bar.tone}
                      title={entry.booking.pickupAddress}
                      onClick={() => void open(entry.booking.orderId)}
                    >
                      <span className="font-mono font-semibold tracking-[0.03em]">
                        {entry.booking.reference}
                      </span>
                    </Bar>
                  </span>
                ))}
              </Track>

              {rows.map((row) => (
                <span key={row.driver.driverId} className="contents">
                  <Lane
                    label={
                      <span
                        className={cx(
                          'inline-flex items-center gap-[7px]',
                          row.kind === 'busy'
                            ? 'text-warn'
                            : row.kind === 'break'
                              ? 'text-ink-3'
                              : 'text-ok',
                        )}
                      >
                        <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-current" />
                        <span className="font-semibold text-ink truncate">
                          {row.driver.displayName}
                        </span>
                        {row.driver.callSign !== null && (
                          <span className="text-ink-3">· {row.driver.callSign}</span>
                        )}
                      </span>
                    }
                  />
                  <Track nowPct={window.nowPct} ticks={window.ticks.map((tick) => tick.pct)}>
                    {row.bars.map((bar, at) => (
                      <Bar
                        key={at}
                        from={bar.from}
                        to={bar.to}
                        tone={bar.tone}
                        dashed={bar.dashed}
                        hollow={bar.hollow}
                        title={bar.title}
                        onClick={
                          bar.orderId === undefined
                            ? undefined
                            : () => void open(bar.orderId as string)
                        }
                      >
                        {bar.label === 'break' ? (
                          t('timeline.break')
                        ) : bar.label === '' ? (
                          '→'
                        ) : (
                          <span className="font-mono font-semibold tracking-[0.03em]">
                            {bar.label}
                          </span>
                        )}
                      </Bar>
                    ))}

                    {row.notes.map((note, at) => (
                      <Note key={at} at={note.at} tone={note.tone}>
                        {noteText(note.text, row, formats, t)}
                      </Note>
                    ))}

                    {/* A shift's end, where the system knows one: the moment the sweep would
                        sign this driver off. */}
                    {row.marker !== null && (
                      <>
                        <Marker
                          at={row.marker.at}
                          title={t('timeline.shiftEnds', {
                            time: formats.time(row.driver.shiftCapAt),
                          })}
                        />
                        <Note at={row.marker.at} tone="danger" top={2}>
                          {t('timeline.shiftEnds', {
                            time: formats.time(row.driver.shiftCapAt),
                          })}
                        </Note>
                      </>
                    )}

                    {/* The seconds an offer has left, which is the one number on this screen
                        that a dispatcher acts on within the minute. */}
                    {row.driver.offer !== null && (
                      <Note at={window.pct(row.driver.offer.expiresAt)} tone="brand">
                        {t('timeline.offered', {
                          reference: row.driver.offer.reference,
                          seconds: secondsLeft(row.driver.offer, window.now),
                        })}
                      </Note>
                    )}
                  </Track>
                </span>
              ))}
            </Timeline>
          </div>
        )}

        <div className="flex gap-6 px-3.5 py-2.5 mt-auto border-t border-rule text-secondary">
          {strip(snapshot).map((item) => (
            <span key={item.key}>
              <span
                className={cx(
                  'font-semibold tabular-nums',
                  item.key === 'freeNow' && item.value > 0 ? 'text-ok' : '',
                  item.key === 'waitingOver10Min' && item.value > 0 ? 'text-danger' : '',
                )}
              >
                {item.value}
              </span>{' '}
              <span className="text-ink-3">{t(`timeline.strip.${item.key}`)}</span>
            </span>
          ))}
        </div>
      </Card>

      {/* The order form over the timeline, so a caller is never lost while looking at it. */}
      <Dialog
        open={taking}
        title={t('order.newOrder')}
        onClose={() => setTaking(false)}
        wide
      >
        <OrderEntry
          key={fromCall?.callId ?? 'blank'}
          prefill={fromCall}
          lastTaken={lastTaken}
          onCreated={(order) => {
            onCreated(order);
            setTaking(false);
          }}
        />
      </Dialog>

      {/* The same detail the desk uses, so assigning from here works exactly as it does
          there. A real <dialog>, which is also what keeps the keys 1-3 refused inside it. */}
      <Dialog
        open={selectedOrder !== null}
        title={selectedOrder?.reference ?? ''}
        onClose={() => setSelectedOrder(null)}
        wide
      >
        {selectedOrder !== null && (
          <OrderDetail
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onChanged={() => void open(selectedOrder.id)}
            onCancelled={() => {
              setSelectedOrder(null);
              void refresh();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** The words on a note, which depend on which note it is. */
function noteText(
  kind: string,
  row: ReturnType<typeof driverRows>[number],
  formats: { time: (iso: string) => string },
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (kind === 'freeSince') {
    const since = formats.time(row.driver.shiftStateSince);

    return row.driver.rankName === null
      ? t('timeline.freeSince', { time: since })
      : t('timeline.freeSinceAt', { time: since, rank: row.driver.rankName });
  }

  if (kind === 'freeAt' && row.driver.job !== null) {
    return t('timeline.freeAt', { time: formats.time(row.driver.job.expectedEndAt) });
  }

  return '';
}

/**
 * The call in progress, as one line at the top.
 *
 * With a button that opens the order form over the timeline: a dispatcher watching this view
 * when the telephone rings must not have to leave it, because leaving it is how a caller
 * gets lost.
 */
function CallStrip({ onNewOrder }: { onNewOrder: () => void }) {
  const { t } = useTranslation();
  const { call, registration, hold, hangUp } = usePhone();

  // A firm with no telephone system gets no telephone anywhere, exactly as on the desk: a
  // line saying nobody is on the line is noise on a console that can never have one.
  if (registration.state === 'failed' && registration.code === 'telephony.not_enabled') {
    return null;
  }

  if (call.state !== 'up') {
    return (
      <Card className="flex items-center gap-3 px-3.5 py-2 text-ink-3">
        <Icon name="phone" />
        {t('softphone.idle')}
      </Card>
    );
  }

  return (
    <Card tone="brand" className="flex items-center justify-between gap-3 px-3.5 py-2">
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex items-center gap-2 text-[13px] font-semibold shrink-0">
          <Icon name="phone" />
          {t('timeline.onCall')}
        </span>
        <span className="truncate">{call.peer}</span>
      </div>

      <div className="flex gap-1.5 shrink-0">
        <Button size="sm" variant="outline" onClick={() => hold(!call.held)}>
          {t('softphone.hold')}
        </Button>
        <Button size="sm" variant="danger" onClick={hangUp}>
          {t('softphone.hangUp')}
        </Button>
        <Button size="sm" variant="primary" className="ml-1.5" onClick={onNewOrder}>
          {t('timeline.newOrderForCaller')}
        </Button>
      </div>
    </Card>
  );
}

/** Waiting, booked, free now — three counts in one line. */
function Counts({ snapshot }: { snapshot: TimelineSnapshot }) {
  const { t } = useTranslation();

  return (
    <Card className="flex items-center">
      <div className="px-3.5 py-1.5 border-r border-rule">
        <div className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
          {t('timeline.waiting')}
        </div>
        <div className="font-semibold tabular-nums">{snapshot.waiting.length}</div>
      </div>
      <div className="px-3.5 py-1.5 border-r border-rule">
        <div className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
          {t('timeline.booked')}
        </div>
        <div className="font-semibold tabular-nums">{snapshot.booked.length}</div>
      </div>
      <div className="px-3.5 py-1.5">
        <div className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
          {t('timeline.strip.freeNow')}
        </div>
        <div className="font-semibold tabular-nums text-ok">{snapshot.totals.freeNow}</div>
      </div>
    </Card>
  );
}

/** What the shapes mean. The console is used by people trained on the phone in an afternoon. */
function Legend() {
  const { t } = useTranslation();

  const items: { key: string; look: string }[] = [
    { key: 'ride', look: 'bg-ok-soft border border-ok' },
    { key: 'toPickup', look: 'bg-transparent border border-dashed border-ok' },
    { key: 'offered', look: 'bg-brand-soft border border-dashed border-brand' },
    { key: 'break', look: 'bg-surface-3 border border-transparent' },
  ];

  return (
    <ul className="flex items-center gap-3.5 m-0 p-0 list-none text-secondary text-ink-3">
      {items.map((item) => (
        <li key={item.key} className="inline-flex items-center gap-1.5">
          <span className={cx('w-3.5 h-2.5 rounded-[2px]', item.look)} aria-hidden="true" />
          {t(`timeline.legend.${item.key}`)}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span className="w-0.5 h-3 bg-danger" aria-hidden="true" />
        {t('timeline.legend.shiftEnds')}
      </li>
    </ul>
  );
}
